import React, { useId } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface PaginationProps {
  page: number;
  totalPages: number;
  pageSize: number;
  pageSizeOptions: number[];
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

/**
 * Reusable pagination component with:
 * - Previous/Next buttons
 * - Page indicator: "Page X of Y"
 * - Page size dropdown
 * - Record count: "Showing X-Y of Z"
 * - Responsive layout
 */
export const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  pageSize,
  pageSizeOptions,
  total,
  onPageChange,
  onPageSizeChange,
}) => {
  const { isMobile } = useMediaQuery();
  const id = useId();
  const selectId = `${id}-page-size-select`;

  if (total === 0) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <div style={isMobile ? styles.containerMobile : styles.container}>
      {/* Record count */}
      <span style={styles.recordCount}>
        Showing{' '}
        <span style={styles.recordCountNumber}>
          {start.toLocaleString()}-{end.toLocaleString()}
        </span>{' '}
        of <span style={styles.recordCountNumber}>{total.toLocaleString()}</span>
      </span>

      {/* Navigation */}
      <div style={styles.nav}>
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          style={styles.navButton}
          aria-label="Previous page"
        >
          <ChevronLeft size={16} aria-hidden="true" />
          {!isMobile && <span>Previous</span>}
        </button>

        <span style={styles.pageInfo}>
          Page <span style={styles.pageInfoNumber}>{page}</span> of{' '}
          <span style={styles.pageInfoNumber}>{totalPages}</span>
        </span>

        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          style={styles.navButton}
          aria-label="Next page"
        >
          {!isMobile && <span>Next</span>}
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      </div>

      {/* Page size selector */}
      <div style={styles.pageSizeContainer}>
        <label htmlFor={selectId} style={styles.pageSizeLabel}>
          Per page:
        </label>
        <select
          id={selectId}
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          style={styles.pageSizeSelect}
          aria-label="Items per page"
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    marginTop: '1rem',
    padding: '0.75rem 1rem',
    backgroundColor: 'var(--surface-color)',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--border-color)',
    fontSize: '0.875rem',
  } as React.CSSProperties,
  containerMobile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.75rem',
    marginTop: '1rem',
    padding: '0.75rem 1rem',
    backgroundColor: 'var(--surface-color)',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--border-color)',
    fontSize: '0.875rem',
  } as React.CSSProperties,
  recordCount: {
    color: 'var(--text-secondary)',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  recordCountNumber: {
    color: 'var(--text-primary)',
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
  } as React.CSSProperties,
  nav: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  } as React.CSSProperties,
  navButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.375rem 0.75rem',
    backgroundColor: 'var(--background-color)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius)',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  pageInfo: {
    color: 'var(--text-secondary)',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  pageInfoNumber: {
    color: 'var(--text-primary)',
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
  } as React.CSSProperties,
  pageSizeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  pageSizeLabel: {
    color: 'var(--text-secondary)',
    fontSize: '0.875rem',
  } as React.CSSProperties,
  pageSizeSelect: {
    padding: '0.25rem 0.5rem',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius)',
    backgroundColor: 'var(--background-color)',
    color: 'var(--text-primary)',
    fontSize: '0.875rem',
    cursor: 'pointer',
  } as React.CSSProperties,
};
